import { movies, type Movie } from "@/mocks/movies";
import { genres, type Genre } from "@/mocks/genres";

export type SortKey = "release" | "rating" | "popularity";

export const allMovies: Movie[] = movies;

export function getMovieById(id: string | undefined): Movie | undefined {
  if (!id) return undefined;
  return movies.find((m) => m.id === id);
}

export function getGenre(slug: string | undefined): Genre | undefined {
  if (!slug) return undefined;
  return genres.find((g) => g.slug === slug);
}

export function getGenreLabel(slug: string): string {
  return getGenre(slug)?.label ?? slug;
}

export function formatDate(dateStr: string): string {
  const parts = dateStr.split("-");
  if (parts.length !== 3) return dateStr;
  return `${parts[0]}年${Number(parts[1])}月${Number(parts[2])}日`;
}

export function formatRuntime(minutes: number): string {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return `${h}小时${m}分钟`;
}

export function getLatestMovies(limit?: number): Movie[] {
  const sorted = [...movies].sort((a, b) => (a.releaseDate < b.releaseDate ? 1 : -1));
  return typeof limit === "number" ? sorted.slice(0, limit) : sorted;
}

export function getHotMovies(limit?: number): Movie[] {
  const sorted = [...movies].sort((a, b) => b.popularity - a.popularity);
  return typeof limit === "number" ? sorted.slice(0, limit) : sorted;
}

export function getTopRatedMovies(limit?: number): Movie[] {
  const sorted = [...movies].sort((a, b) => b.rating - a.rating);
  return typeof limit === "number" ? sorted.slice(0, limit) : sorted;
}

export function getRelatedMovies(movie: Movie, limit = 4): Movie[] {
  const byGenre = movies.filter(
    (m) => m.id !== movie.id && m.genres.some((g) => movie.genres.includes(g))
  );
  const byDirector = movies.filter(
    (m) => m.id !== movie.id && m.director === movie.director && !byGenre.includes(m)
  );
  return [...byGenre, ...byDirector].slice(0, limit);
}

export interface MovieFilter {
  genres?: string[];
  year?: number | "all";
  minRating?: number;
}

export function filterMovies(list: Movie[], filter: MovieFilter): Movie[] {
  return list.filter((m) => {
    if (filter.genres && filter.genres.length > 0) {
      if (!m.genres.some((g) => filter.genres!.includes(g))) return false;
    }
    if (filter.year && filter.year !== "all" && m.year !== filter.year) return false;
    if (typeof filter.minRating === "number" && m.rating < filter.minRating) return false;
    return true;
  });
}

export function sortMovies(list: Movie[], key: SortKey): Movie[] {
  const copy = [...list];
  if (key === "rating") return copy.sort((a, b) => b.rating - a.rating);
  if (key === "popularity") return copy.sort((a, b) => b.popularity - a.popularity);
  return copy.sort((a, b) => (a.releaseDate < b.releaseDate ? 1 : -1));
}

export function searchMovies(keyword: string): Movie[] {
  const kw = keyword.trim().toLowerCase();
  if (!kw) return [];
  return movies.filter(
    (m) =>
      m.title.toLowerCase().includes(kw) ||
      m.enTitle.toLowerCase().includes(kw) ||
      m.director.toLowerCase().includes(kw) ||
      m.genres.some((g) => getGenreLabel(g).toLowerCase().includes(kw))
  );
}

export const allYears: number[] = Array.from(new Set(movies.map((m) => m.year))).sort(
  (a, b) => b - a
);